import { useEffect, useState, type ReactNode } from 'react';
import { Link, Outlet, useRouter } from '@tanstack/react-router';
import { hasPermission, useMe } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { CommandPalette } from './CommandPalette';

interface NavItem {
  to: string;
  label: string;
  permission?: string;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/pos', label: 'POS', permission: 'pos.checkout' },
  { to: '/cash-drawer', label: 'Arqueo de Caja', permission: 'pos.checkout' },
  { to: '/orders', label: 'Órdenes de Servicio', permission: 'os.read' },
  { to: '/inventory', label: 'Inventario', permission: 'inventory.read' },
  { to: '/warehouses', label: 'Bodegas', permission: 'inventory.read' },
  { to: '/stock-take', label: 'Toma de Inventario', permission: 'inventory.write' },
  { to: '/customers', label: 'Clientes', permission: 'os.read' },
  { to: '/invoices', label: 'Facturas', permission: 'accounting.read' },
  { to: '/accounting', label: 'Contabilidad', permission: 'accounting.read' },
  { to: '/payroll', label: 'Nómina y SD', permission: 'accounting.read' },
  { to: '/users', label: 'Usuarios', permission: 'settings.read' },
  { to: '/audit', label: 'Auditoría', permission: 'settings.read' },
  { to: '/settings', label: 'Configuración', permission: 'settings.read' },
];

export function AppShell({ children }: { children?: ReactNode }) {
  const me = useMe();
  const router = useRouter();
  const currentPath = router.state.location.pathname;
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [currentPath]);

  // Close drawer on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const visible = NAV.filter((n) => !n.permission || hasPermission(me.data, n.permission));

  return (
    <div className="app-shell">
      {/* Mobile top bar — visible only on small screens via CSS */}
      <header className="topbar">
        <button
          className="topbar__burger"
          aria-label="Abrir menú"
          onClick={() => setDrawerOpen(true)}
        >
          ☰
        </button>
        <span className="topbar__title">Horology OS</span>
      </header>

      <aside
        className={`sidebar ${drawerOpen ? 'sidebar--open' : ''}`}
        aria-hidden={!drawerOpen}
      >
        <h1 className="sidebar__brand">Horology OS</h1>
        <nav className="sidebar__nav">
          {visible.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={currentPath === item.to ? 'active' : ''}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="sidebar__footer">
          {me.data && (
            <div style={{ fontSize: '0.85rem', color: 'var(--color-muted)', marginBottom: '0.5rem' }}>
              {me.data.fullName}
              <br />
              <small>{me.data.role}</small>
            </div>
          )}
          <button
            className="btn"
            style={{ width: '100%' }}
            onClick={() => {
              void supabase.auth.signOut();
            }}
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      {drawerOpen && (
        <div
          className="drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className="main">{children ?? <Outlet />}</main>
      <CommandPalette />
    </div>
  );
}
